<template>
    <div class="create-session-page">
        <div class="page-header">
            <div class="header-left">
                <button class="btn-back" @click="goBack">
                    <i class="pi pi-arrow-left"></i>
                </button>
                <div>
                    <h1>New Reconciliation Session</h1>
                    <p class="subtitle">Select a template and activities to reconcile</p>
                </div>
            </div>
            <div class="header-actions">
                <b-button variant="outline-secondary" @click="goBack">
                    Cancel
                </b-button>
                <b-button 
                    variant="primary" 
                    :disabled="!canCreate || creating"
                    @click="createSession"
                >
                    <b-spinner v-if="creating" small class="mr-2"></b-spinner>
                    {{ creating ? 'Creating...' : 'Create & Run Matching' }}
                </b-button>
            </div>
        </div>

        <div class="session-form">
            <div class="form-section">
                <h2>1. Select Template</h2>
                <p class="section-hint">Choose a reconciliation template that defines matching rules</p>
                
                <div class="template-grid">
                    <div
                        v-for="template in templates"
                        :key="template.id"
                        class="template-option"
                        :class="{ selected: form.template_id === template.id }"
                        @click="selectTemplate(template)"
                    >
                        <div class="template-header">
                            <span class="type-badge" :class="'type-' + template.type.toLowerCase()">
                                {{ formatType(template.type) }}
                            </span>
                            <span v-if="getMatchMode(template)" class="match-mode-badge">
                                {{ getMatchModeLabel(template) }}
                            </span>
                            <i v-if="form.template_id === template.id" class="pi pi-check-circle selected-icon"></i>
                        </div>
                        <h3>{{ template.name }}</h3>
                        <p>{{ template.description || 'No description' }}</p>
                        <div class="template-stats">
                            <span>{{ template.rules_config?.rules?.length || 0 }} rules</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-section">
                <h2>2. Session Details</h2>
                <div class="form-group">
                    <label>Session Name</label>
                    <input
                        v-model="form.name"
                        type="text"
                        class="form-input"
                        placeholder="Optional: Auto-generated if empty"
                    />
                </div>
            </div>

            <div class="form-section">
                <h2>3. Select Activities</h2>
                <p class="section-hint">{{ activitySelectionHint }}</p>

                <div v-if="matchMode === 'many_to_one'" class="many-to-one-layout">
                    <div class="one-side">
                        <div class="card-label">
                            <span class="side-badge side-a">A</span>
                            Source Activity (Bank Statement)
                        </div>
                        <ActivitySelector
                            v-model="form.source_order_id"
                            placeholder="Search source activity..."
                            :order-type-id="selectedTemplate?.source_order_type_id"
                            @activity-selected="sourceActivity = $event"
                        />
                        <div v-if="sourceActivity" class="selected-single-activity">
                            <div class="single-activity-info">
                                <span class="activity-ref">{{ sourceActivity.reference_no }}</span>
                                <span class="activity-total">{{ formatCurrency(sourceActivity.total) }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="vs-divider vertical">
                        <div class="vs-line"></div>
                        <span>MATCHES</span>
                        <div class="vs-line"></div>
                    </div>

                    <div class="many-side">
                        <div class="card-label">
                            <span class="side-badge side-b">B</span>
                            Target Activities (Many)
                        </div>
                        <div class="selection-mode-toggle">
                            <button 
                                class="mode-btn" 
                                :class="{ active: targetSelectionMode === 'type' }"
                                @click="targetSelectionMode = 'type'"
                            >
                                <i class="pi pi-folder"></i>
                                By Activity Type
                            </button>
                            <button 
                                class="mode-btn" 
                                :class="{ active: targetSelectionMode === 'manual' }"
                                @click="targetSelectionMode = 'manual'"
                            >
                                <i class="pi pi-list"></i>
                                Select Specific
                            </button>
                        </div>

                        <div v-if="targetSelectionMode === 'type'" class="type-selection">
                            <label>Select Activity Type</label>
                            <select v-model="form.target_order_type_id" class="form-select">
                                <option :value="null">-- Select Activity Type --</option>
                                <option v-for="type in orderTypes" :key="type.id" :value="type.id">
                                    {{ type.name }}
                                </option>
                            </select>
                            <div v-if="form.target_order_type_id && targetTypeOrders.length > 0" class="type-preview">
                                <div class="type-preview-header">
                                    <span>{{ targetTypeOrders.length }} activities found</span>
                                    <strong>{{ formatCurrency(targetTypeTotalAmount) }}</strong>
                                </div>
                                <div class="type-orders-list">
                                    <div v-for="order in targetTypeOrders.slice(0, 5)" :key="order.id" class="type-order-item">
                                        <span>{{ order.reference_no }}</span>
                                        <span>{{ formatCurrency(order.grand_total || order.total_price) }}</span>
                                    </div>
                                    <div v-if="targetTypeOrders.length > 5" class="more-indicator">
                                        +{{ targetTypeOrders.length - 5 }} more...
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else class="multi-select-container">
                            <ActivitySelector
                                placeholder="Search and add activities..."
                                :order-type-id="selectedTemplate?.target_order_type_id"
                                :multiple="true"
                                @activity-selected="addTargetActivity"
                            />
                            <div v-if="selectedTargetActivities.length > 0" class="selected-activities-list">
                                <div 
                                    v-for="activity in selectedTargetActivities" 
                                    :key="activity.id"
                                    class="selected-activity-chip"
                                >
                                    <div class="chip-info">
                                        <span class="chip-ref">{{ activity.reference_no }}</span>
                                        <span class="chip-total">{{ formatCurrency(activity.total) }}</span>
                                    </div>
                                    <button class="chip-remove" @click="removeTargetActivity(activity.id)">
                                        <i class="pi pi-times"></i>
                                    </button>
                                </div>
                                <div class="activities-total">
                                    <span>Total:</span>
                                    <strong>{{ formatCurrency(targetTotalAmount) }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="matchMode === 'one_to_many'" class="one-to-many-layout">
                    <div class="one-side">
                        <div class="card-label">
                            <span class="side-badge side-a">A</span>
                            Source Activity (Single)
                        </div>
                        <ActivitySelector
                            v-model="form.source_order_id"
                            placeholder="Search source activity..."
                            :order-type-id="selectedTemplate?.source_order_type_id"
                            @activity-selected="sourceActivity = $event"
                        />
                        <div v-if="sourceActivity" class="selected-single-activity">
                            <div class="single-activity-info">
                                <span class="activity-ref">{{ sourceActivity.reference_no }}</span>
                                <span class="activity-total">{{ formatCurrency(sourceActivity.total) }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="vs-divider vertical">
                        <div class="vs-line"></div>
                        <span>MATCHES</span>
                        <div class="vs-line"></div>
                    </div>

                    <div class="many-side">
                        <div class="card-label">
                            <span class="side-badge side-b">B</span>
                            Target Activities (Many)
                        </div>
                        <div class="selection-mode-toggle">
                            <button 
                                class="mode-btn" 
                                :class="{ active: targetSelectionMode === 'type' }"
                                @click="targetSelectionMode = 'type'"
                            >
                                <i class="pi pi-folder"></i>
                                By Activity Type
                            </button>
                            <button 
                                class="mode-btn" 
                                :class="{ active: targetSelectionMode === 'manual' }"
                                @click="targetSelectionMode = 'manual'"
                            >
                                <i class="pi pi-list"></i>
                                Select Specific
                            </button>
                        </div>

                        <div v-if="targetSelectionMode === 'type'" class="type-selection">
                            <label>Select Activity Type</label>
                            <select v-model="form.target_order_type_id" class="form-select">
                                <option :value="null">-- Select Activity Type --</option>
                                <option v-for="type in orderTypes" :key="type.id" :value="type.id">
                                    {{ type.name }}
                                </option>
                            </select>
                            <div v-if="form.target_order_type_id && targetTypeOrders.length > 0" class="type-preview">
                                <div class="type-preview-header">
                                    <span>{{ targetTypeOrders.length }} activities found</span>
                                    <strong>{{ formatCurrency(targetTypeTotalAmount) }}</strong>
                                </div>
                                <div class="type-orders-list">
                                    <div v-for="order in targetTypeOrders.slice(0, 5)" :key="order.id" class="type-order-item">
                                        <span>{{ order.reference_no }}</span>
                                        <span>{{ formatCurrency(order.grand_total || order.total_price) }}</span>
                                    </div>
                                    <div v-if="targetTypeOrders.length > 5" class="more-indicator">
                                        +{{ targetTypeOrders.length - 5 }} more...
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else class="multi-select-container">
                            <ActivitySelector
                                placeholder="Search and add activities..."
                                :order-type-id="selectedTemplate?.target_order_type_id"
                                :multiple="true"
                                @activity-selected="addTargetActivity"
                            />
                            <div v-if="selectedTargetActivities.length > 0" class="selected-activities-list">
                                <div 
                                    v-for="activity in selectedTargetActivities" 
                                    :key="activity.id"
                                    class="selected-activity-chip"
                                >
                                    <div class="chip-info">
                                        <span class="chip-ref">{{ activity.reference_no }}</span>
                                        <span class="chip-total">{{ formatCurrency(activity.total) }}</span>
                                    </div>
                                    <button class="chip-remove" @click="removeTargetActivity(activity.id)">
                                        <i class="pi pi-times"></i>
                                    </button>
                                </div>
                                <div class="activities-total">
                                    <span>Total:</span>
                                    <strong>{{ formatCurrency(targetTotalAmount) }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="one-to-one-layout">
                    <div class="activity-card">
                        <div class="card-label">
                            <span class="side-badge side-a">A</span>
                            Source Activity
                        </div>
                        <ActivitySelector
                            v-model="form.source_order_id"
                            placeholder="Search source activity..."
                            :order-type-id="selectedTemplate?.source_order_type_id"
                            @activity-selected="sourceActivity = $event"
                        />
                    </div>

                    <div class="vs-divider">
                        <span>VS</span>
                    </div>

                    <div class="activity-card">
                        <div class="card-label">
                            <span class="side-badge side-b">B</span>
                            Target Activity
                        </div>
                        <ActivitySelector
                            v-model="form.target_order_id"
                            placeholder="Search target activity..."
                            :order-type-id="selectedTemplate?.target_order_type_id"
                            @activity-selected="targetActivity = $event"
                        />
                    </div>
                </div>
            </div>

            <div v-if="selectedTemplate" class="form-section preview-section">
                <h2>4. Preview</h2>
                <div class="preview-card">
                    <div class="preview-row">
                        <span class="preview-label">Template:</span>
                        <span class="preview-value">{{ selectedTemplate.name }}</span>
                    </div>
                    <div class="preview-row">
                        <span class="preview-label">Match Mode:</span>
                        <span class="preview-value">{{ getMatchModeLabel(selectedTemplate) }}</span>
                    </div>
                    <div class="preview-row">
                        <span class="preview-label">Matching Rules:</span>
                        <span class="preview-value">{{ selectedTemplate.rules_config?.rules?.length || 0 }} rules</span>
                    </div>
                    <div class="preview-row">
                        <span class="preview-label">Source (A):</span>
                        <span class="preview-value">
                            {{ sourceActivity?.reference_no || 'Not selected' }}
                            <span v-if="sourceActivity" class="item-count">({{ sourceActivity.order_items_count || '?' }} items)</span>
                        </span>
                    </div>
                    <div class="preview-row">
                        <span class="preview-label">Target (B):</span>
                        <span class="preview-value">
                            <template v-if="(matchMode === 'one_to_many' || matchMode === 'many_to_one') && targetSelectionMode === 'type'">
                                {{ targetTypeOrders.length }} activities by type ({{ formatCurrency(targetTypeTotalAmount) }})
                            </template>
                            <template v-else-if="(matchMode === 'one_to_many' || matchMode === 'many_to_one') && targetSelectionMode === 'manual'">
                                {{ selectedTargetActivities.length }} activities ({{ formatCurrency(targetTotalAmount) }})
                            </template>
                            <template v-else>
                                {{ targetActivity?.reference_no || 'Not selected' }}
                                <span v-if="targetActivity" class="item-count">({{ targetActivity.order_items_count || '?' }} items)</span>
                            </template>
                        </span>
                    </div>
                    <div v-if="matchMode !== 'one_to_one'" class="preview-row highlight">
                        <span class="preview-label">Expected Match:</span>
                        <span class="preview-value" :class="amountMatchClass">
                            {{ formatCurrency(sourceTotalAmount) }} vs {{ formatCurrency(targetTotalAmount) }}
                            <span v-if="amountDifference !== 0" class="diff-badge">
                                {{ amountDifference > 0 ? '+' : '' }}{{ formatCurrency(amountDifference) }}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ActivitySelector from '@/components/Recon/ActivitySelector.vue'

export default {
    name: 'CreateSession',
    components: {
        ActivitySelector
    },
    data() {
        return {
            creating: false,
            form: {
                template_id: null,
                name: '',
                source_order_id: null,
                target_order_id: null,
                target_order_type_id: null
            },
            sourceActivity: null,
            targetActivity: null,
            selectedTargetActivities: [],
            targetSelectionMode: 'type',
            orderTypes: [],
            targetTypeOrders: []
        }
    },
    computed: {
        ...mapGetters('recon', ['templates']),
        selectedTemplate() {
            if (!this.form.template_id) return null
            return this.templates.find(t => t.id === this.form.template_id)
        },
        matchMode() {
            return this.selectedTemplate?.rules_config?.matching_strategy?.match_mode || 'one_to_one'
        },
        activitySelectionHint() {
            switch (this.matchMode) {
                case 'many_to_one':
                    return 'Select a bank statement/source, then choose target activities by type or select specific ones'
                case 'one_to_many':
                    return 'Select a source activity, then choose target activities by type or select specific ones'
                default:
                    return 'Select one source and one target activity to compare'
            }
        },
        sourceTotalAmount() {
            return parseFloat(this.sourceActivity?.total) || 0
        },
        targetTotalAmount() {
            if ((this.matchMode === 'one_to_many' || this.matchMode === 'many_to_one') && this.targetSelectionMode === 'manual') {
                return this.selectedTargetActivities.reduce((sum, a) => sum + (parseFloat(a.total) || 0), 0)
            }
            return parseFloat(this.targetActivity?.total) || 0
        },
        targetTypeTotalAmount() {
            return this.targetTypeOrders.reduce((sum, o) => sum + (parseFloat(o.grand_total) || parseFloat(o.total_price) || 0), 0)
        },
        amountDifference() {
            return this.sourceTotalAmount - this.targetTotalAmount
        },
        amountMatchClass() {
            const diff = Math.abs(this.amountDifference)
            if (diff < 0.01) return 'match-exact'
            if (diff < 1) return 'match-close'
            return 'match-diff'
        },
        canCreate() {
            if (!this.form.template_id) return false
            if (!this.form.source_order_id) return false

            if (this.matchMode === 'many_to_one' || this.matchMode === 'one_to_many') {
                if (this.targetSelectionMode === 'type') {
                    return this.form.target_order_type_id && this.targetTypeOrders.length > 0
                } else {
                    return this.selectedTargetActivities.length > 0
                }
            }
            return this.form.target_order_id
        }
    },
    watch: {
        'form.target_order_type_id'(typeId) {
            if (typeId) {
                this.fetchOrdersByType(typeId)
            } else {
                this.targetTypeOrders = []
            }
        }
    },
    mounted() {
        this.$store.dispatch('recon/fetchTemplates')
        this.fetchOrderTypes()
    },
    methods: {
        selectTemplate(template) {
            this.form.template_id = template.id
            this.resetSelections()
        },
        resetSelections() {
            this.form.source_order_id = null
            this.form.target_order_id = null
            this.form.target_order_type_id = null
            this.sourceActivity = null
            this.targetActivity = null
            this.selectedTargetActivities = []
            this.targetTypeOrders = []
            this.targetSelectionMode = 'type'
        },
        async fetchOrderTypes() {
            try {
                const response = await this.$http.get('/api/order-types')
                this.orderTypes = response.data.data || response.data || []
            } catch (err) {
                console.error('Failed to fetch order types:', err)
            }
        },
        async fetchOrdersByType(typeId) {
            try {
                const response = await this.$http.get('/api/orders', {
                    params: {
                        order_type_id: typeId,
                        per_page: 500,
                        status: 'active'
                    }
                })
                this.targetTypeOrders = response.data.data?.data || response.data.data || []
            } catch (err) {
                console.error('Failed to fetch orders by type:', err)
                this.targetTypeOrders = []
            }
        },
        addTargetActivity(activity) {
            if (!this.selectedTargetActivities.find(a => a.id === activity.id)) {
                this.selectedTargetActivities.push(activity)
            }
        },
        removeTargetActivity(id) {
            this.selectedTargetActivities = this.selectedTargetActivities.filter(a => a.id !== id)
        },
        async createSession() {
            if (!this.canCreate) return

            this.creating = true
            try {
                const payload = {
                    template_id: this.form.template_id,
                    name: this.form.name
                }

                payload.source_order_id = this.form.source_order_id

                if (this.matchMode === 'many_to_one' || this.matchMode === 'one_to_many') {
                    if (this.targetSelectionMode === 'type') {
                        payload.target_order_type_id = this.form.target_order_type_id
                    } else {
                        payload.target_order_ids = this.selectedTargetActivities.map(a => a.id)
                    }
                } else {
                    payload.target_order_id = this.form.target_order_id
                }

                const session = await this.$store.dispatch('recon/createSession', payload)
                
                await this.$store.dispatch('recon/runMatching', session.id)
                
                this.$bvToast.toast('Session created and matching completed', {
                    title: 'Success',
                    variant: 'success'
                })
                
                this.$router.push(`/ams/reconciliation/sessions/${session.id}`)
            } catch (err) {
                this.$bvToast.toast(err.response?.data?.message || 'Failed to create session', {
                    title: 'Error',
                    variant: 'danger'
                })
            } finally {
                this.creating = false
            }
        },
        getMatchMode(template) {
            return template?.rules_config?.matching_strategy?.match_mode
        },
        getMatchModeLabel(template) {
            const mode = this.getMatchMode(template)
            const labels = {
                'one_to_one': '1:1',
                'one_to_many': '1:N',
                'many_to_one': 'N:1'
            }
            return labels[mode] || '1:1'
        },
        formatType(type) {
            const types = {
                INVOICE_PO: 'PO vs Invoice',
                BANK_LEDGER: 'Bank vs Ledger',
                BANK_INVOICES: 'Bank vs Invoices',
                PO_INVOICE: 'PO vs Invoice',
                INVOICE_PAYMENTS: 'Invoice vs Payments',
                ACTIVITY_COMPARE: 'Activity Compare',
                PORTFOLIO_BROKER: 'Portfolio vs Broker',
                CUSTOM: 'Custom'
            }
            return types[type] || type
        },
        formatCurrency(value) {
            if (value === null || value === undefined) return '$0.00'
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(value)
        },
        goBack() {
            this.$router.push('/ams/reconciliation/sessions')
        }
    }
}
</script>

<style scoped>
.create-session-page {
    padding: 24px;
    max-width: 1100px;
    margin: 0 auto;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.btn-back {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.btn-back:hover {
    border-color: #2F80ED;
    color: #2F80ED;
}

.header-left h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #111827;
}

.subtitle {
    margin: 4px 0 0 0;
    color: #6b7280;
    font-size: 14px;
}

.header-actions {
    display: flex;
    gap: 12px;
}

.session-form {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.form-section {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
}

.form-section h2 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.section-hint {
    margin: 0 0 20px 0;
    font-size: 14px;
    color: #6b7280;
}

.template-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
}

.template-option {
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.2s;
}

.template-option:hover {
    border-color: #2F80ED;
}

.template-option.selected {
    border-color: #2F80ED;
    background: #eff6ff;
}

.template-header {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 12px;
}

.type-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 4px;
    text-transform: uppercase;
}

.match-mode-badge {
    font-size: 10px;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 4px;
    background: #f3e8ff;
    color: #7c3aed;
}

.type-invoice_po, .type-po_invoice {
    background: #dbeafe;
    color: #1d4ed8;
}

.type-bank_ledger, .type-bank_invoices {
    background: #dcfce7;
    color: #15803d;
}

.type-invoice_payments {
    background: #fef3c7;
    color: #b45309;
}

.type-activity_compare {
    background: #e0e7ff;
    color: #4338ca;
}

.type-portfolio_broker {
    background: #fef3c7;
    color: #b45309;
}

.type-custom {
    background: #f3e8ff;
    color: #7c3aed;
}

.selected-icon {
    color: #2F80ED;
    font-size: 18px;
    margin-left: auto;
}

.template-option h3 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
}

.template-option p {
    margin: 0 0 12px 0;
    font-size: 13px;
    color: #6b7280;
    line-height: 1.4;
}

.template-stats {
    font-size: 12px;
    color: #9ca3af;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-group label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
}

.form-input {
    padding: 10px 14px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    max-width: 400px;
}

.form-input:focus {
    border-color: #2F80ED;
    box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.1);
}

.one-to-one-layout {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 24px;
    align-items: start;
}

.many-to-one-layout,
.one-to-many-layout {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 24px;
    align-items: start;
}

.many-side {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.one-side {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.activity-card {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.card-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
}

.count-badge {
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    background: #e5e7eb;
    border-radius: 10px;
    color: #6b7280;
}

.side-badge {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
    color: white;
}

.side-a {
    background: #2F80ED;
}

.side-b {
    background: #10b981;
}

.vs-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 40px;
}

.vs-divider span {
    font-size: 14px;
    font-weight: 700;
    color: #9ca3af;
    background: #f3f4f6;
    padding: 8px 12px;
    border-radius: 20px;
}

.vs-divider.vertical {
    flex-direction: column;
    gap: 8px;
    padding-top: 20px;
}

.vs-line {
    width: 2px;
    height: 40px;
    background: #e5e7eb;
}

.multi-select-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.selected-activities-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 300px;
    overflow-y: auto;
    padding: 12px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
}

.selected-activity-chip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

.chip-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.chip-ref {
    font-weight: 600;
    font-size: 13px;
    color: #111827;
}

.chip-total {
    font-size: 12px;
    color: #6b7280;
}

.chip-remove {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: #fee2e2;
    color: #dc2626;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.chip-remove:hover {
    background: #dc2626;
    color: white;
}

.activities-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #eff6ff;
    border-radius: 8px;
    margin-top: 8px;
    font-size: 14px;
    color: #1d4ed8;
}

.activities-total strong {
    font-size: 16px;
}

.selected-single-activity {
    padding: 12px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
}

.single-activity-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.activity-ref {
    font-weight: 600;
    color: #111827;
}

.activity-total {
    font-weight: 600;
    color: #059669;
}

.preview-section {
    background: #f9fafb;
}

.preview-card {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.preview-row {
    display: flex;
    gap: 12px;
}

.preview-row.highlight {
    padding: 12px;
    background: white;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
}

.preview-label {
    font-size: 14px;
    color: #6b7280;
    min-width: 140px;
}

.preview-value {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    display: flex;
    align-items: center;
    gap: 8px;
}

.item-count {
    font-weight: 400;
    color: #6b7280;
}

.diff-badge {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
}

.match-exact .diff-badge {
    background: #dcfce7;
    color: #15803d;
}

.match-close .diff-badge {
    background: #fef3c7;
    color: #b45309;
}

.match-diff .diff-badge {
    background: #fee2e2;
    color: #dc2626;
}

.mr-2 {
    margin-right: 8px;
}

.selection-mode-toggle {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
}

.mode-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s;
}

.mode-btn:hover {
    border-color: #2F80ED;
    color: #2F80ED;
}

.mode-btn.active {
    border-color: #2F80ED;
    background: #eff6ff;
    color: #2F80ED;
}

.type-selection {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.type-selection label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
}

.form-select {
    padding: 10px 14px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    background: white;
}

.form-select:focus {
    border-color: #2F80ED;
    box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.1);
}

.type-preview {
    padding: 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

.type-preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 12px;
    font-size: 14px;
    color: #374151;
}

.type-preview-header strong {
    color: #059669;
}

.type-orders-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 180px;
    overflow-y: auto;
}

.type-order-item {
    display: flex;
    justify-content: space-between;
    padding: 8px 10px;
    background: white;
    border-radius: 6px;
    font-size: 13px;
    color: #374151;
}

.more-indicator {
    text-align: center;
    font-size: 12px;
    color: #6b7280;
    padding: 8px;
    font-style: italic;
}
</style>
